import { useState, useEffect } from 'react';
import { logger, base64 } from './../utils/utils';
import {
  permissionLevel,
  category,
  permissionGroup,
  label,
  dimension,
  processStatus,
  issueCommentType,
  issue,
} from './../data';

/**
 * Hook caution about parameter of hook function
 * Don't implement-pass parameter of hook function with object
 * (Object, pass by refernce couse of useEffect depedencies by parameter is changed ref)
 */

/**
 * =============================================
 *  Hook IssueCommentType
 * ---------------------------------------------
 *  MODEL: IssueCommentType
 * =============================================
 */

/**
 * useIssueCommentTypeGetListAll()
 * Hook: Get all issue comment type
 * @param lang language of data, default: TH
 * @returns
 */
export function useIssueCommentTypeGetListAll(lang = 'TH') {
  // logger
  logger('useIssueCommentTypeGetListAll render');

  const [data, setData] = useState([]);
  useEffect(() => {
    issueCommentType
      .getListAll({ lang })
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use issue-comment-type-get-list-all error, ', error);
      });
  }, [lang]);
  return data;
}

/**
 * userIssueCreateId() , get issue id shortId and createDate
 * without dependency
 */
export function useIssueCreateId() {
  // logger
  logger('useIssueCreateId render');

  const [data, setData] = useState([]);
  useEffect(() => {
    issue
      .createId()
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use issue-create-id error, ', error);
      });
  }, []);
  return data;
}

/**
 * =============================================
 *  Hook ProcessStatus
 * ---------------------------------------------
 *  MODEL: ProcessStatus
 * =============================================
 */

/**
 * useProcessStatusGetListAll()
 * Hook:Get list processStatus all
 * @param lang language of data, default: TH
 * @returns
 */
export function useProcessStatusGetListAll(lang = 'TH') {
  // logger
  logger('useProcessStatusGetListAll render');

  const [data, setData] = useState([]);
  useEffect(() => {
    const params = {
      package: base64.encodeObj({
        lang,
      }),
    };
    processStatus
      .getListAll(params)
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use process-status-get-list-all error, ', error);
      });
  }, [lang]);
  return data;
}

/**
 * useProcessStatusGetList() v2
 * Hook:Get list processStatus (with Business Status Logic)
 * @param {string} lang  language of data, default: 'TH'
 * @param {string} issue_id issue identifier, default: ''
 * @returns
 */
export function useProcessStatusGetList(
  lang = 'TH',
  issue_id = '',
  processStatus_id = ''
) {
  // logger
  logger('useProcessStatusGetList render');

  const [data, setData] = useState([]);
  useEffect(() => {
    const params = {
      package: base64.encodeObj({
        lang,
        issue_id,
      }),
    };
    processStatus
      .getList(params)
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use process-status-get-list error, ', error);
      });
  }, [lang, issue_id, processStatus_id]);
  return data;
}

/**
 * =============================================
 *  Hook PermissionGroup
 * ---------------------------------------------
 *  MODEL: PermissionGroup
 * =============================================
 */

export function usePermissionGroupGetListByCategoryLevel(
  category_id,
  level
) {
  // logger
  logger('usePermissionGroupGetListByCategoryLevel render');

  const [data, setData] = useState([]);
  useEffect(() => {
    const params = {
      package: base64.encodeObj({
        category_id,
        level,
      }),
    };
    permissionGroup
      .getListByCategoryLevel(params)
      .then((result) => {
        // data must not null for setData
        if (result) setData(result);
      })
      .catch((error) => {
        console.error(
          'use permission-group-get-list-by-category-level error, ',
          error
        );
      });
  }, [category_id, level]);
  return data;
}

/**
 * =============================================
 *  Hook PermissionLevel
 * ---------------------------------------------
 *  MODEL: PermissionLevel
 * =============================================
 */

/**
 * usePermissionLevelGetListAllSetting(), Don't pass parameter of hook with object (object, pass by refernce couse of useEffect depedencies by parameter is changed ref)
 * Hook:Get all permission level except level 0 for setting page
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
export function usePermissionLevelGetListAllSetting(lang = 'TH') {
  const [data, setData] = useState([]);
  // logger
  logger('usePermissionLevelGetListAllSetting render');

  useEffect(() => {
    permissionLevel
      .getListAllSetting({ lang })
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error(
          'use permission-level-get-list-all-setting error, ',
          error
        );
      });
  }, [lang]);
  return data;
}

/**
 * usePermissionLevelGetListAllSetting(), Don't pass parameter of hook with object (object, pass by refernce couse of useEffect depedencies by parameter is changed ref)
 * Hook:Get all permission level except level 0 for setting page
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
 export function usePermissionLevelGetListAllWithUserRight(lang = 'TH') {
  const [data, setData] = useState([]);
  // logger
  logger('usePermissionLevelGetListAllWithUserRight render');

  useEffect(() => {
    permissionLevel
      .getListAllWithUserRight({ lang })
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error(
          'use permission-level-get-list-all-user-right error, ',
          error
        );
      });
  }, [lang]);
  return data;
}


/**
 * =============================================
 *  Hook Category
 * ---------------------------------------------
 *  MODEL: Category
 * =============================================
 */

/**
 * useCategoryGetListAll
 * Hook: Category getListAll
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
export function useCategoryGetListAll(lang = 'TH') {
  // logger
  logger('useCategoryGetListAll render');

  const [data, setData] = useState([]);
  useEffect(() => {
    category
      .getListAll({ lang })
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use category-get-list-all error, ', error);
      });
  }, [lang]);
  return data;
}

/** useCategoryGetList()  getList category by permission (for staff)
 * Hook: Category getList
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
export function useCategoryGetList(lang = 'TH') {
  // logger
  logger('useCategoryGetList render');

  const [data, setData] = useState([]);
  useEffect(() => {
    category
      .getList({
        package: base64.encodeObj({
          lang,
        }),
      })
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use category-get-list error, ', error);
      });
  }, [lang]);
  return data;
}

/** useCategoryGetListByMode()
 * Hook: Category getList
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
export function useCategoryGetListByMode(lang = 'TH', mode = 'reporter') {
  // logger
  logger('useCategoryGetListByMode render');

  const [data, setData] = useState([]);
  useEffect(() => {
    try {
      switch (mode) {
        case 'staff':
          category
            .getList({
              package: base64.encodeObj({
                lang,
              }),
            })
            .then((result) => {
              //map data nest category to flat category
              let categorys = result.map((item) => {
                let category = item.categorys.categorys;
                return {
                  id: category.id,
                  name: category.name,
                  description: category.description,
                  permissionLevel: item.permissionLevel,
                };
              });
              setData(categorys);
            })
            .catch((error) => {
              throw error;
            });
          break;
        default:
          category
            .getListAll({
              lang,
            })
            .then((result) => {
              setData(result);
            })
            .catch((error) => {
              throw error;
            });
      }
    } catch (error) {
      console.error('use category-get-list-by-mode error, ', error);
    }
  }, [lang, mode]);
  return data;
}

/**
 * =============================================
 *  Hook Labal
 * ---------------------------------------------
 *  MODEL: Label
 * =============================================
 */
/**
 * useLabelGetListAll
 * Hook: Label getListAll
 * @returns
 */
export function useLabelGetListAll() {
  // logger
  logger('useLabelGetListAll render');

  const [data, setData] = useState([]);
  useEffect(() => {
    label
      .getListAll()
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use label-get-list-all error, ', error);
      });
  }, []);
  return data;
}

/**
 * =============================================
 *  Hook Dimeission
 * ---------------------------------------------
 *  MODEL: Dimeission
 * =============================================
 */

/**
 * useDimensionGetListAll
 * Hook: Dimenison getListAll
 * @param {string} lang  language of data, default: 'TH'
 * @returns
 */
export function useDimensionGetListAll(lang = 'TH') {
  // logger
  logger('useDimensionGetListAll render');

  const [data, setData] = useState([]);
  useEffect(() => {
    dimension
      .getListAll()
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('use dimension-get-list-all error, ', error);
      });
  }, []);
  return data;
}
